import { io, Socket } from 'socket.io-client';
import { WorkerSocketService, createWorkerSocket } from './worker-socket.service';
import { EventEnvelope, createEventEnvelope, BaseEvent } from 'nexgen-types';
import { useSessionStore } from '@host/stores/session.store';
import { useUserProfileStore } from '@host/stores/user-profile.store';
import { useGuestStore, useWorkplaceStore } from '@host/stores';
import { HealthPingEvent, UserContextSyncEvent } from 'nexgen-types/events';
import { GlobalEventService } from '@host/services/event.service';
import { Logger } from '../../shared/services/logger.service';
import { getDeviceId } from '@host/services/device-id.service';

// ===================================================================
// TYPES & CONFIGURATION
// ===================================================================

type SocketEventHandler = (event: BaseEvent) => void;

interface SocketContext {
  session_id: string;
  user_pid?: string;
  org_pid?: string;
  floor_pid?: string;
  room_pid?: string;
  host_pid?: string;
  guest_pid?: string;
  // NGE-841: read server-side by extractUserContext() (handler.util.ts) — needed
  // so the disconnect signal (activity.disconnect) can carry a device_id.
  device_id?: string;
}

interface ConnectionStatus {
  connected: boolean;
  socketId?: string;
  namespace: string;
}

interface ConnectionStatus {
  connected: boolean;
  socketId?: string;
  namespace: string;
  workerReady?: boolean;
}

const SOCKET_CONFIG = {
  host: import.meta.env.VITE_EVENT_SOCKET_HOST || 'http://localhost:5550',
  path: import.meta.env.VITE_EVENT_SOCKET_PATH || '/socket.io',
  transport: ['websocket'] as string[],
  autoConnect: true,
};

const DEDUP_CONFIG = {
  maxEventsPerSocket: 100,
  cleanupIntervalMs: 60000, // 1 minute
  eventExpiryMs: 30000, // 30 seconds
};

const CONNECTION_CONFIG = {
  defaultNamespace: '/events',
  reconnectOnVisibility: true,
  healthCheckInterval: 60000, // 60 seconds
  performInitialHealthCheck: false,
};

// Update the interface to be simpler (add after existing interfaces)
interface ConnectionOptions {
  namespace?: string;
  reconnectOnVisibility?: boolean;
  healthCheckInterval?: number;
  performInitialHealthCheck?: boolean;
}

// ===================================================================
// SOCKET SERVICE CLASS
// ===================================================================

export class SocketService {
  private static instance: SocketService;
  private sockets: Map<string, Socket> = new Map();
  private eventHandlers: Map<string, SocketEventHandler> = new Map();
  private noHandlerEventTypes: Set<string> = new Set();
  private noHandlerAttempts: Map<string, number> = new Map();

  // Event deduplication tracking
  private processedEvents: Map<string, Set<string>> = new Map(); // socketId -> Set of event keys
  private eventTimestamps: Map<string, Map<string, number>> = new Map(); // socketId -> eventKey -> timestamp
  private cleanupInterval?: number;
  private filteredSessionEvents = 0;

  // Topic subscription tracking
  private subscribedTopics: Set<string> = new Set();
  private subscriptionInitialized = false;

  // Once destroy() runs (e.g. on session-replaced eviction) this flag prevents
  // any further socket creation/reconnect for the lifetime of the page. The
  // singleton remains in memory but is inert until the next full page load.
  private isDestroyed = false;

  private constructor() {
    this.startCleanupInterval();
  }

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  /**
   * Initialize connection with default configuration
   * This is the main method to call for standard app connection setup
   */
  async initializeConnection(options: ConnectionOptions = {}): Promise<{
    success: boolean;
    socket?: Socket;
    connectionStatus: ConnectionStatus;
    error?: Error;
  }> {
    if (this.isDestroyed) {
      Logger.debug('[SocketService] initializeConnection skipped — service destroyed');
      return {
        success: false,
        connectionStatus: { connected: false } as ConnectionStatus,
        error: new Error('SocketService is destroyed'),
      };
    }

    const config = {
      namespace: options.namespace || CONNECTION_CONFIG.defaultNamespace,
      reconnectOnVisibility:
        options.reconnectOnVisibility ?? CONNECTION_CONFIG.reconnectOnVisibility,
      healthCheckInterval: options.healthCheckInterval ?? CONNECTION_CONFIG.healthCheckInterval,
      performInitialHealthCheck:
        options.performInitialHealthCheck ?? CONNECTION_CONFIG.performInitialHealthCheck,
    };

    Logger.debug(`[SocketService] 🚀 Initializing connection:`, {
      namespace: config.namespace,
      autoConnect: SOCKET_CONFIG.autoConnect,
      performHealthCheck: config.performInitialHealthCheck,
    });

    try {
      // Get/create socket connection (uses existing SOCKET_CONFIG)
      const socket = this.getSocket(config.namespace);

      // Wait for connection if autoConnect is enabled (from existing SOCKET_CONFIG)
      if (SOCKET_CONFIG.autoConnect) {
        await this.waitForConnection(socket);
      }

      Logger.debug(`[SocketService] ✅ Connection initialized:`, {
        namespace: config.namespace,
        connected: socket.connected,
        socketId: socket.id,
      });

      // Perform initial health check
      if (config.performInitialHealthCheck) {
        try {
          const healthCheckResult = await this.checkHealth(config.namespace, 3000);
          Logger.debug(`[SocketService] 🏥 Initial health check:`, healthCheckResult);
        } catch (error) {
          console.warn(`[SocketService] ⚠️ Initial health check failed:`, error);
        }
      }

      return {
        success: true,
        socket,
        connectionStatus: this.getConnectionStatus(config.namespace),
      };
    } catch (error) {
      console.error(`[SocketService] ❌ Connection initialization failed:`, error);
      return {
        success: false,
        connectionStatus: this.getConnectionStatus(config.namespace),
        error: error as Error,
      };
    }
  }

  /**
   * Check and ensure connection is healthy
   * Call this periodically or on page visibility changes
   */
  async ensureConnection(namespace = '/events'): Promise<boolean> {
    if (this.isDestroyed) {
      Logger.debug('[SocketService] ensureConnection skipped — service destroyed');
      return false;
    }
    try {
      const status = this.getConnectionStatus(namespace);

      // If not connected, get socket but don't force recreation
      if (!status.connected) {
        Logger.debug(
          `[SocketService] 🔄 Connection not detected, getting socket for ${namespace}...`
        );

        const socket = this.getSocket(namespace);

        // Wait for connection if it's not already connected
        if (!socket.connected) {
          await this.waitForConnection(socket);
        }

        return socket.connected;
      }

      // Connection is already good
      console.debug(`[SocketService] ✅ Connection already healthy for ${namespace}`);
      return true;
    } catch (error) {
      console.error(`[SocketService] ❌ ensureConnection failed:`, error);
      return false;
    }
  }

  // ===================================================================
  // CONTEXT MANAGEMENT
  // ===================================================================

  /**
   * Get current user context from application stores
   * TODO: Consider consolidating these stores into a single context provider
   */
  private getSocketContext(): SocketContext {
    const sessionStore = useSessionStore.getState();
    const userProfileStore = useUserProfileStore.getState();
    const workplaceStore = useWorkplaceStore.getState();
    const guestStore = useGuestStore.getState();

    const context: SocketContext = {
      session_id: sessionStore.getOrCreateSessionId(),
      user_pid: userProfileStore.profile?.pid,
      org_pid: sessionStore.orgPid ?? undefined,
      floor_pid: workplaceStore.presence?.floor_pid,
      room_pid: workplaceStore.presence?.room_pid,
      host_pid: guestStore.guest?.host_pid,
      guest_pid: guestStore.guest?.pid,
      device_id: getDeviceId(),
    };

    Logger.debug('[SocketService] 🔍 Socket context for connection:', {
      ...context,
      has_room_pid: !!context.room_pid,
      presence: workplaceStore.presence,
    });
    Logger.debug('[SocketService] Context:', context);
    return context;
  }

  /**
   * Check if user context is available (user is logged in)
   */
  private hasUserContext(): boolean {
    const context = this.getSocketContext();
    Logger.debug('🚀 ~ SocketService ~ context:', context);
    return !!((context.user_pid || context.guest_pid) && context.org_pid);
  }

  /**
   * Build socket.io connection options with authentication context
   */
  private getConnectionOptions() {
    const context = this.getSocketContext();

    // Build query object, only including defined values
    const query: Record<string, string> = {
      session_id: context.session_id,
    };

    // Only include user_pid if it's actually defined
    if (context.user_pid) {
      query.user_pid = context.user_pid;
    }

    if (context.guest_pid) {
      query.guest_pid = context.guest_pid;
    }

    return {
      path: SOCKET_CONFIG.path,
      transports: SOCKET_CONFIG.transport,
      autoConnect: SOCKET_CONFIG.autoConnect,
      auth: context,
      query,
    };
  }

  /**
   * Get current connection configuration
   */
  getConnectionConfig(): {
    socket: typeof SOCKET_CONFIG;
    deduplication: typeof DEDUP_CONFIG;
    connection: typeof CONNECTION_CONFIG;
  } {
    return {
      socket: SOCKET_CONFIG,
      deduplication: DEDUP_CONFIG,
      connection: CONNECTION_CONFIG,
    };
  }

  // ===================================================================
  // SOCKET CONNECTION MANAGEMENT
  // ===================================================================

  /**
   * Get or create socket connection for namespace with connection validation
   * Users the worker socket service to avoid browser throttling
   */
  getSocket(namespace = '/events'): Socket {
    if (this.isDestroyed) {
      throw new Error(
        '[SocketService] Service has been destroyed; reload the page to re-establish sockets'
      );
    }

    const normalizedNamespace = namespace.startsWith('/') ? namespace : `/${namespace}`;

    // Check if we already have a socket
    if (this.sockets.has(normalizedNamespace)) {
      const existingSocket = this.sockets.get(normalizedNamespace)!;

      // For direct sockets, only check if it's connected - NO WORKER STATUS CHECK
      if (existingSocket.connected) {
        console.debug(
          `[SocketService] ✅ Reusing existing socket for ${normalizedNamespace} (${existingSocket.id})`
        );
        return existingSocket;
      } else {
        Logger.debug(`[SocketService] 🧹 Socket exists but disconnected, cleaning up...`);
        this.cleanupSocket(normalizedNamespace, existingSocket);
      }
    }

    // Create new socket only if none exists or previous was disconnected
    Logger.debug(`[SocketService] 🆕 Creating new socket for ${normalizedNamespace}`);
    this.createSocket(normalizedNamespace);

    return this.sockets.get(normalizedNamespace)!;
  }

  /**
   * Create new socket connection with event handlers
   * Use worker socket instead of direct socket.io
   */
  private createSocket(namespace: string): void {
    Logger.debug(`[SocketService] Current sockets:`, Array.from(this.sockets.keys()));

    const connectionOptions = this.getConnectionOptions();

    try {
      // 🔧 TEMPORARY: Use direct socket.io instead of worker for debugging
      Logger.debug(`[SocketService] 🔧 DEBUG: Using direct socket.io connection`);

      const directSocket = io(`${SOCKET_CONFIG.host}${namespace}`, connectionOptions);

      this.setupSocketEventHandlers(directSocket, namespace);
      this.sockets.set(namespace, directSocket);

      Logger.debug(`[SocketService] ✅ Direct socket created for ${namespace}`);

      // Monitor connection
      directSocket.on('connect', () => {
        Logger.debug(`[SocketService] 🎉 Direct socket connected: ${directSocket.id}`);
      });

      directSocket.on('connect_error', (error) => {
        console.error(`[SocketService] 💥 Direct socket connection error:`, error);
      });
    } catch (error) {
      console.error(`[SocketService] ❌ Socket creation failed:`, error);

      // Fallback: try worker socket
      Logger.debug(`[SocketService] 🔄 Falling back to worker socket...`);

      const workerSocket = createWorkerSocket(SOCKET_CONFIG.host, namespace, connectionOptions);

      workerSocket
        ._connect({
          host: SOCKET_CONFIG.host,
          namespace,
          path: SOCKET_CONFIG.path,
          auth: connectionOptions.auth,
        })
        .then(() => {
          Logger.debug(`[SocketService] ✅ Worker socket connected for ${namespace}`);
        })
        .catch((workerError) => {
          console.error(`[SocketService] ❌ Worker socket connection failed:`, workerError);
        });

      this.setupSocketEventHandlers(workerSocket as any, namespace);
      this.sockets.set(namespace, workerSocket as any);
    }
  }

  /**
   * Setup standard socket event handlers (connect, disconnect, messages)
   */
  private setupSocketEventHandlers(socket: Socket, namespace: string): void {
    socket.on('connect', () => {
      Logger.debug(`[SocketService] ✅ Connected to ${namespace} (${socket.id})`);
      if (socket.id) {
        this.initializeDeduplicationForSocket(socket.id);
        // Initialize topic subscriptions when socket connects
        this.initializeTopicSubscriptions();
      }
    });

    socket.on('connect_error', (err) => {
      console.error(`[SocketService] ❌ Connection error (${namespace}):`, err?.message || err);
    });

    socket.on('disconnect', (reason) => {
      console.warn(`[SocketService] ⚠️ Disconnected from ${namespace}: ${reason}`);
      if (socket.id) {
        this.cleanupSocketDeduplication(socket.id);
      }
    });

    // 🔍 TRACE ALL OUTGOING MESSAGES (dev only — avoids overhead in production)
    if (import.meta.env.DEV) {
      const originalEmit = socket.emit.bind(socket);
      socket.emit = function (event: string, ...args: any[]) {
        const hasNull = args.some((arg) => arg === null || arg === undefined);
        const hasEmpty = args.length === 0;

        if (hasNull || hasEmpty || args.some((arg) => arg === '')) {
          console.error(`[SocketService] 🚨 SENDING PROBLEMATIC MESSAGE:`, {
            event,
            args,
            socketId: socket.id,
            argsTypes: args.map((arg) => typeof arg),
            hasNull,
            hasEmpty,
            stackTrace: new Error('Message source').stack,
          });
        } else {
          Logger.debug(`[SocketService] 📤 [${namespace}] Sending:`, {
            event,
            messageType: args[0]?.__type || args[0]?.event_type || 'unknown',
            socketId: socket.id,
            argCount: args.length,
          });
        }

        return originalEmit(event, ...args);
      };
    }

    // Complete incoming event handler implementation
    const handleIncomingEvent = (envelope: EventEnvelope<any>) => {
      try {
        Logger.debug(`[SocketService] 📥 [${namespace}] Received:`, envelope.event_type);

        // 🔍 DEBUG LOG FOR INCOMING EVENTS (dev only)
        if (import.meta.env.DEV) {
          Logger.debug(`[SocketService] 📥 INCOMING SESSION DEBUG:`, {
            eventType: envelope.event_type,
            envelopeSessionId: envelope.session_id,
            currentSessionId: this.getSocketContext().session_id,
            fullEnvelope: envelope,
          });
        }

        // 1. Handle system events first
        if (this.isNoHandlerError(envelope)) {
          this.handleNoHandlerError(envelope);
          return;
        }

        // 2. Session filtering
        if (this.isOwnSessionEvent(envelope)) {
          this.filteredSessionEvents++;
          console.debug(`[SocketService] 🔄 Filtered own session event: ${envelope.event_type}`);
          return;
        }

        // 3. Deduplication check (skip for livecaptions - they should always be processed)
        if (
          socket.id &&
          !envelope.event_type?.includes('livecaptions') &&
          this.isDuplicateEvent(socket.id, envelope)
        ) {
          console.debug(`[SocketService] 🔄 Filtered duplicate event: ${envelope.event_type}`);
          return;
        }

        // 4. Mark as processed (only for non-livecaptions to avoid memory bloat)
        if (socket.id && !envelope.event_type?.includes('livecaptions')) {
          this.markEventAsProcessed(socket.id, envelope);
        }

        // 5. Route to EventService via emit
        const eventData = envelope.data.payload || envelope.data;
        if (eventData && envelope.event_type) {
          // Set the eventData __type to match the topic
          eventData.__type = envelope.event_type;
          GlobalEventService.emit(envelope.event_type, eventData, false); // false = don't re-broadcast
        }
      } catch (error) {
        console.error(`[SocketService] ❌ Error handling incoming event:`, {
          error: error instanceof Error ? error.message : String(error),
          envelope: envelope.event_type,
          namespace,
        });
      }
    };

    // Register for multiple event types that the server might send
    socket.on('event', handleIncomingEvent);
    socket.on('message', handleIncomingEvent);

    // Handle acknowledgments and errors
    socket.on('ack', (data: any) => {
      Logger.debug(`[SocketService] ✅ [${namespace}] ACK:`, data?.event_type || 'unknown');
    });

    socket.on('nack', (data: any) => {
      console.error(`[SocketService] ❌ [${namespace}] NACK:`, data?.message || data);
    });

    // Handle event-ack (from server BaseHandler)
    socket.on('event-ack', (data: any) => {
      // Forward to event handlers
      if (data?.event_type) {
        const handler = this.eventHandlers.get(namespace);
        if (handler) {
          const ackEvent = {
            __type: `${data.event_type}.ack`,
            ...data,
          };
          handler(ackEvent as any);
        }
      }
    });
  }

  /**
   * Initialize topic subscriptions based on user context
   */
  setEventHandler(namespace: string, handler: SocketEventHandler): void {
    Logger.debug(`[SocketService] Registered handler for: ${namespace}`);
    this.eventHandlers.set(namespace, handler);
  }

  // ===================================================================
  // SESSION FILTERING SYSTEM
  // ===================================================================

  /**
   * Check if incoming event is from our own session
   */
  private isOwnSessionEvent(envelope: EventEnvelope<any>): boolean {
    const currentContext = this.getSocketContext();

    // Check BOTH context and metadata for session ID
    const incomingSessionId = envelope.session_id;

    Logger.debug(`[SocketService] 🔍 SESSION FILTER CHECK:`, {
      eventType: envelope.event_type,
      currentSession: currentContext.session_id,
      incomingSession: incomingSessionId,
      hasIncomingSession: !!incomingSessionId,
      sessionsMatch: incomingSessionId === currentContext.session_id,
      willFilter: !!incomingSessionId && incomingSessionId === currentContext.session_id,
    });

    // If no session ID in envelope, allow it through
    if (!incomingSessionId) {
      return false;
    }

    // Only filter if BOTH session and socket ID match — this is a self-emitted echo.
    // Server-dispatched events (e.g. forced leave) carry our session_id for routing
    // but have a different socket_id (the events service adapter), so they must pass through.
    if (incomingSessionId !== currentContext.session_id) {
      return false;
    }

    const currentSocketId = this.getSocket()?.id;
    const envelopeSocketId = (envelope as any).socket_id;
    return !!envelopeSocketId && envelopeSocketId === currentSocketId;
  }

  /**
   * Get session filtering statistics
   */
  getSessionFilteringStats(): { filteredEvents: number } {
    return {
      filteredEvents: this.filteredSessionEvents,
    };
  }

  /**
   * Reset session filtering statistics
   */
  resetSessionFilteringStats(): void {
    this.filteredSessionEvents = 0;
    Logger.debug('[SocketService] 🧹 Reset session filtering stats');
  }

  // ===================================================================
  // EVENT DEDUPLICATION SYSTEM
  // ===================================================================

  /**
   * Initialize deduplication tracking for a socket
   */
  private initializeDeduplicationForSocket(socketId: string): void {
    if (!this.processedEvents.has(socketId)) {
      this.processedEvents.set(socketId, new Set());
      this.eventTimestamps.set(socketId, new Map());
      console.debug(`[SocketService] 🆕 Initialized deduplication for socket: ${socketId}`);
    }
  }

  /**
   * Generate unique key for event deduplication
   * TODO:  Change the topic key generation to use the original event data
   */
  private generateEventKey(envelope: EventEnvelope<any>): string {
    let key = '';

    // Exception: livecaptions events should always be processed (use UUID instead of timestamp)
    if (envelope.event_type?.includes('livecaptions')) {
      key = `${envelope.event_type}-${envelope.uuid}`;
      return key;
    }

    // For topic events, use the original event data to prevent duplicates
    if (envelope.event_type?.endsWith('.topic')) {
      key = `${envelope.event_type}-${envelope.timestamp}`;
    } else {
      key = `${envelope.event_type}-${envelope.uuid}`;
    }
    Logger.debug(`[SocketService] TEST KEY: ${key}`);
    return key;
  }

  /**
   * Check if event is a duplicate
   */
  private isDuplicateEvent(socketId: string, envelope: EventEnvelope<any>): boolean {
    const eventKey = this.generateEventKey(envelope);
    const socketEvents = this.processedEvents.get(socketId);

    if (!socketEvents) {
      this.initializeDeduplicationForSocket(socketId);
      return false;
    }

    // Check for recent duplicate (within expiry window)
    const timestamps = this.eventTimestamps.get(socketId);
    if (timestamps?.has(eventKey)) {
      const eventTime = timestamps.get(eventKey)!;
      const now = Date.now();

      if (now - eventTime < DEDUP_CONFIG.eventExpiryMs) {
        return true; // Recent duplicate
      } else {
        // Expired, remove it
        socketEvents.delete(eventKey);
        timestamps.delete(eventKey);
      }
    }

    return socketEvents.has(eventKey);
  }

  /**
   * Mark event as processed
   */
  private markEventAsProcessed(socketId: string, envelope: EventEnvelope<any>): void {
    const eventKey = this.generateEventKey(envelope);
    const socketEvents = this.processedEvents.get(socketId);
    const timestamps = this.eventTimestamps.get(socketId);

    if (socketEvents && timestamps) {
      socketEvents.add(eventKey);
      timestamps.set(eventKey, Date.now());

      // Prevent memory leaks - remove oldest events if too many
      if (socketEvents.size > DEDUP_CONFIG.maxEventsPerSocket) {
        const oldestKeys = Array.from(timestamps.entries())
          .sort((a, b) => a[1] - b[1])
          .slice(0, socketEvents.size - DEDUP_CONFIG.maxEventsPerSocket)
          .map(([key]) => key);

        oldestKeys.forEach((key) => {
          socketEvents.delete(key);
          timestamps.delete(key);
        });

        console.debug(
          `[SocketService] 🧹 Cleaned ${oldestKeys.length} old events for socket: ${socketId}`
        );
      }
    }
  }

  /**
   * Clean up a socket properly
   */
  private cleanupSocket(namespace: string, socket: Socket): void {
    try {
      this.cleanupSocketDeduplication(socket.id);
      socket.removeAllListeners();

      // Only disconnect if socket is not already disconnected/disconnecting
      // This prevents "WebSocket is closed before the connection is established" errors
      if (socket.connected || socket.active) {
        socket.disconnect();
      }

      this.sockets.delete(namespace);
      Logger.debug(`[SocketService] ✅ Socket cleaned up for ${namespace}`);
    } catch (error) {
      console.error(`[SocketService] ❌ Error cleaning up socket:`, error);
      // Even on error, remove from map to prevent stale references
      this.sockets.delete(namespace);
    }
  }

  /**
   * Clean up deduplication data for disconnected socket
   */
  private cleanupSocketDeduplication(socketId: string | undefined): void {
    if (!socketId) return;
    this.processedEvents.delete(socketId);
    this.eventTimestamps.delete(socketId);
    console.debug(`[SocketService] 🧹 Cleaned up deduplication for socket: ${socketId}`);
  }

  /**
   * Start periodic cleanup of expired events
   */
  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredEvents();
    }, DEDUP_CONFIG.cleanupIntervalMs);
  }

  /**
   * Clean up expired events across all sockets
   */
  private cleanupExpiredEvents(): void {
    const now = Date.now();
    let totalCleaned = 0;

    this.eventTimestamps.forEach((timestamps, socketId) => {
      const socketEvents = this.processedEvents.get(socketId);
      if (!socketEvents) return;

      const expiredKeys: string[] = [];

      timestamps.forEach((timestamp, eventKey) => {
        if (now - timestamp > DEDUP_CONFIG.eventExpiryMs) {
          expiredKeys.push(eventKey);
        }
      });

      expiredKeys.forEach((key) => {
        socketEvents.delete(key);
        timestamps.delete(key);
        totalCleaned++;
      });
    });

    if (totalCleaned > 0) {
      console.debug(`[SocketService] 🧹 Periodic cleanup: removed ${totalCleaned} expired events`);
    }
  }

  // ===================================================================
  // TOPIC SUBSCRIPTION MANAGEMENT
  // ===================================================================

  /**
   * Initialize topic subscriptions when socket connects
   */
  private async initializeTopicSubscriptions(): Promise<void> {
    try {
      Logger.debug('[SocketService] 🚀 Initializing topic subscriptions...');

      // Check if user context is available
      if (!this.hasUserContext()) {
        console.warn(
          '[SocketService] ⚠️ No user context available, skipping subscription initialization'
        );
        console.warn('[SocketService] ⚠️ Subscriptions will be initialized after user login');
        return;
      }

      // Get current context
      const context = this.getSocketContext();
      const orgPid = context.org_pid;

      if (!orgPid) {
        console.warn(
          '[SocketService] ⚠️ No org_pid available, will retry when context is available'
        );
        return;
      }

      // 🔧 NEW: Emit context sync event to backend to establish proper subscriptions
      try {
        Logger.debug('[SocketService] 📡 Sending context sync event to backend...');

        const contextSyncEvent = UserContextSyncEvent.create({
          user_pid: context.user_pid!,
          org_pid: context.org_pid!,
          floor_pid: context.floor_pid,
          room_pid: context.room_pid,
          session_id: context.session_id,
          guest_pid: context.guest_pid!,
        });

        // 🔍 DEBUG: Verify event instance before sending
        Logger.debug('[SocketService] 🔍 Context sync event instance:', {
          event: contextSyncEvent,
          isBaseEvent: contextSyncEvent instanceof BaseEvent,
          hasType: '__type' in contextSyncEvent,
          type: contextSyncEvent.__type,
          constructor: contextSyncEvent.constructor.name,
        });

        // Send directly via socket to avoid circular flow through GlobalEventService
        await this.emitEvent('/events', contextSyncEvent);
        Logger.debug('[SocketService] ✅ Context sync event sent successfully');
      } catch (syncError) {
        console.error('[SocketService] ❌ Failed to send context sync event:', syncError);
        // Continue with manual subscription as fallback
      }

      this.subscriptionInitialized = true;
      Logger.debug('[SocketService] ✅ Topic subscriptions initialized');
    } catch (error) {
      console.error('[SocketService] ❌ Failed to initialize subscriptions:', error);
    }
  }

  /**
   * Check if subscriptions need to be initialized and do so if user context is now available
   * This should be called periodically or when user context changes
   */
  async checkAndInitializeSubscriptions(): Promise<void> {
    if (this.subscriptionInitialized) {
      return; // Already initialized
    }

    if (this.hasUserContext()) {
      Logger.debug('[SocketService] 🔄 User context now available, initializing subscriptions...');
      await this.initializeTopicSubscriptions();
    }
  }

  /**
   * Resets subscription state and clears tracked topics, then reinitializes topic subscriptions
   * using the current socket connection. Returns `false` if user context or a connected socket
   * is unavailable, or if reinitialization throws; returns `true` on success.
   */
  async forceReinitializeSubscriptions(): Promise<boolean> {
    if (this.isDestroyed) {
      Logger.debug('[SocketService] forceReinitializeSubscriptions skipped — service destroyed');
      return false;
    }
    Logger.debug('[SocketService] 🔄 Force reinitializing subscriptions...');

    try {
      // Reset subscription state to allow reinitialization
      this.subscriptionInitialized = false;
      this.subscribedTopics.clear();

      // Check if we have user context now
      if (!this.hasUserContext()) {
        console.warn('[SocketService] ⚠️ No user context available for force reinitialization');
        return false;
      }

      // Get the current socket
      const namespace = '/events';
      const socket = this.sockets.get(namespace);

      if (!socket || !socket.connected) {
        console.warn('[SocketService] ⚠️ No connected socket available for reinitialization');
        return false;
      }

      // Initialize subscriptions with current user context
      await this.initializeTopicSubscriptions();

      if (!this.subscriptionInitialized) {
        console.warn('[SocketService] ⚠️ Force reinitialization skipped: missing required context');
        return false;
      }

      Logger.debug('[SocketService] ✅ Force reinitialization completed successfully');
      return true;
    } catch (error) {
      console.error('[SocketService] ❌ Force reinitialization failed:', error);
      return false;
    }
  }

  /**
   * Reinitialize subscriptions when user context becomes available
   * This should be called after user login/authentication
   */
  async reinitializeSubscriptions(): Promise<void> {
    if (this.isDestroyed) {
      Logger.debug('[SocketService] reinitializeSubscriptions skipped — service destroyed');
      return;
    }
    Logger.debug('[SocketService] 🔄 Reinitializing subscriptions with updated context...');

    try {
      // Wait a bit for stores to be updated after login
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Get current context to check if we have user info now
      const context = this.getSocketContext();
      Logger.debug('[SocketService] 🔍 Current context for reinitialization:', context);

      if (!context.user_pid || !context.guest_pid || !context.org_pid) {
        console.warn('[SocketService] ⚠️ No user_pid or org_pid available yet, retrying...');

        // Retry up to 3 times with delays
        for (let i = 0; i < 3; i++) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          const retryContext = this.getSocketContext();

          if ((retryContext.user_pid || retryContext.guest_pid) && retryContext.org_pid) {
            Logger.debug('[SocketService] ✅ User context available after retry', i + 1);
            break;
          }

          if (i === 2) {
            console.error(
              '[SocketService] ❌ Failed to get user context after retries, skipping reinitialization'
            );
            return;
          }
        }
      }

      // Reset subscription state
      this.subscriptionInitialized = false;
      this.subscribedTopics.clear();

      // Force reconnection with new context by disconnecting and reconnecting
      const namespace = '/events';
      const existingSocket = this.sockets.get(namespace);

      if (existingSocket) {
        Logger.debug(
          '[SocketService] 🔌 Disconnecting existing socket to reconnect with user context...'
        );

        // Clean up existing socket
        this.cleanupSocket(namespace, existingSocket);

        // Small delay to ensure cleanup completes
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Create new socket with updated context (this will include user_pid)
      Logger.debug('[SocketService] 🆕 Creating new socket with user context...');
      const newSocket = this.getSocket(namespace);

      // Wait for connection
      await this.waitForConnection(newSocket);

      Logger.debug('[SocketService] ✅ Socket reconnected with user context:', {
        socketId: newSocket.id,
        connected: newSocket.connected,
        userPid: context.user_pid,
      });

      // Initialize subscriptions with the new context
      await this.initializeTopicSubscriptions();

      Logger.debug('[SocketService] ✅ Subscriptions reinitialized successfully');
    } catch (error) {
      console.error('[SocketService] ❌ Failed to reinitialize subscriptions:', error);
    }
  }

  /**
   * Reinitialize socket connection after user login
   * This is the method called by AuthService after successful login
   */
  async reinitializeAfterLogin(): Promise<void> {
    if (this.isDestroyed) {
      Logger.debug('[SocketService] reinitializeAfterLogin skipped — service destroyed');
      return;
    }
    Logger.debug('[SocketService] 🔄 Reinitializing socket connection after login...');

    try {
      // Check if we have user context and subscriptions are initialized
      if (!this.hasUserContext() || !this.subscriptionInitialized) {
        console.warn(
          '[SocketService] ⚠️ No user context or subscriptions not initialized, skipping update'
        );
        return;
      }

      // Get current context
      const context = this.getSocketContext();
      const orgPid = context.org_pid;
      const floorPid = context.floor_pid;

      if (!orgPid) {
        console.warn('[SocketService] ⚠️ No org_pid available for subscription update');
        return;
      }

      // Subscribe to floor-specific topics if floor has changed
      if (floorPid) {
        const floorTopics = [
          `floor.${floorPid}.presence`,
          `floor.${floorPid}.status`,
          `floor.${floorPid}.room-changes`,
        ];

        for (const topic of floorTopics) {
          if (!this.subscribedTopics.has(topic)) {
            await this.subscribeToTopic(topic);
            Logger.debug('[SocketService] ✅ Added subscription for new floor topic:', topic);
          }
        }
      }

      Logger.debug('[SocketService] ✅ Subscriptions updated for context change');
    } catch (error) {
      console.error('[SocketService] ❌ Failed to update subscriptions for context change:', error);
    }
  }

  /**
   * Subscribe to a specific topic
   * Note: The backend handles auto-subscription based on user context during connection
   * This method just tracks what topics we expect to be subscribed to
   */
  private async subscribeToTopic(topic: string): Promise<void> {
    if (this.subscribedTopics.has(topic)) {
      Logger.debug(`[SocketService] ✅ Already tracking subscription to: ${topic}`);
      return;
    }

    try {
      Logger.debug(`[SocketService] 🔔 Tracking subscription to topic: ${topic}`);

      // Track subscription (backend handles actual subscription via auto-subscription)
      this.subscribedTopics.add(topic);

      Logger.debug(`[SocketService] ✅ Now tracking subscription to topic: ${topic}`);
    } catch (error) {
      console.error(`[SocketService] ❌ Failed to track subscription to topic ${topic}:`, error);
    }
  }

  /**
   * Subscribe to a floor's realtime presence topics while merely previewing it
   * (i.e. viewing the floor map without joining a room there).
   *
   * The events service only routes UserLogoutTopic/UserStatusChangedTopic/etc. to
   * sockets that have joined that floor's topic room, and it only joins a socket
   * to a floor when a UserContextSyncEvent (or login/room-change) carries that
   * floor_pid. A pure floor preview never sent one, so previewed-but-not-joined
   * floors never received live presence updates — avatars only refreshed on
   * reload or once the user actually joined a room there.
   *
   * Sending a floor-only sync (no room_pid) is safe: the events service merges
   * it into the socket's existing context rather than replacing it, and topic
   * joins are additive, so this does not disturb the user's real floor/room
   * presence or membership.
   */
  async subscribeToFloorTopics(floorPid: string): Promise<void> {
    try {
      if (!floorPid || !this.hasUserContext()) return;

      const context = this.getSocketContext();
      const orgPid = context.org_pid;
      const userPid = context.user_pid ?? context.guest_pid;
      const guestPid = context.guest_pid ?? context.user_pid;

      if (!orgPid || !userPid || !guestPid) {
        console.warn('[SocketService] ⚠️ Missing context, skipping floor preview sync');
        return;
      }

      Logger.debug(`[SocketService] 🏢 Syncing floor preview context: ${floorPid}`);

      const contextSyncEvent = UserContextSyncEvent.create({
        user_pid: userPid,
        org_pid: orgPid,
        floor_pid: floorPid,
        session_id: context.session_id,
        guest_pid: guestPid,
      });

      await this.emitEvent('/events', contextSyncEvent);

      Logger.debug(`[SocketService] ✅ Floor preview context synced: ${floorPid}`);
    } catch (error) {
      console.error(`[SocketService] ❌ Failed to sync floor preview context:`, error);
    }
  }

  /**
   * Get list of subscribed topics
   */
  getSubscribedTopics(): string[] {
    return Array.from(this.subscribedTopics);
  }

  /**
   * Reset subscriptions (for reconnection scenarios)
   */
  resetSubscriptions(): void {
    this.subscribedTopics.clear();
    this.subscriptionInitialized = false;
    Logger.debug('[SocketService] 🔄 Reset subscriptions');
  }

  // ===================================================================
  // EVENT OPERATIONS
  // ===================================================================

  /**
   * Emit event to namespace (creates envelope from stores + event)
   */
  async emitEvent(namespace: string, eventInstance: BaseEvent): Promise<void> {
    const eventType = eventInstance.__type;

    // Check if this event type is known to have no handler
    if (this.noHandlerEventTypes.has(eventType)) {
      // Increment attempts when skipping (not just when handling error)
      const currentAttempts = this.noHandlerAttempts.get(eventType) || 0;
      const newAttempts = currentAttempts + 1;
      this.noHandlerAttempts.set(eventType, newAttempts);

      console.warn(
        `[SocketService] ⚠️ Skipping ${eventType} - no server handler (${newAttempts} total attempts)`
      );
      return;
    }

    // Proceed with normal emission
    const socket = this.getSocket(namespace);
    await this.waitForConnection(socket);

    const envelope = this.createEventEnvelope(eventInstance, socket.id);

    Logger.debug(`[SocketService] 📤 OUTGOING SESSION DEBUG:`, {
      eventType: eventInstance.__type,
      sessionId: envelope.session_id,
      socketId: socket.id,
      fullContext: envelope,
    });

    console.debug(`[SocketService] 📤 [${namespace}] Emitting: ${eventInstance.__type}`);
    socket.emit('message', envelope);
  }

  /**
   * Create envelope from event + store context
   */
  private createEventEnvelope(
    eventInstance: BaseEvent,
    socketId?: string
  ): EventEnvelope<BaseEvent> {
    const context = this.getSocketContext();

    Logger.debug('🔍 [DEBUG] Creating envelope with context:', context);

    const envelope = createEventEnvelope(eventInstance, {
      session_id: context.session_id,
      user_pid: context.user_pid,
      room_pid: context.room_pid,
      socket_id: socketId,
    });

    Logger.debug('🔍 [DEBUG] Created envelope:', envelope);
    return envelope;
  }

  // ===================================================================
  // UTILITIES
  // ===================================================================

  /**
   * Wait for socket connection to be established
   */
  private async waitForConnection(socket: Socket): Promise<void> {
    if (socket.connected) return;

    return new Promise<void>((resolve) => {
      socket.once('connect', resolve);
    });
  }

  /**
   * Get connection status for debugging
   */
  getConnectionStatus(namespace = '/events'): ConnectionStatus {
    const socket = this.sockets.get(namespace);

    return {
      connected: socket?.connected || false,
      socketId: socket?.id,
      namespace,
    };
  }

  /**
   * Perform health check on namespace
   */
  async checkHealth(namespace: string, timeoutMs = 5000): Promise<{ status: string }> {
    const socket = this.getSocket(namespace);
    await this.waitForConnection(socket);

    return new Promise<{ status: string }>((resolve, reject) => {
      const cleanup = () => {
        socket.off('ack', onAck);
        socket.off('nack', onNack);
        socket.off('disconnect', onDisconnect);
        clearTimeout(timeout);
      };

      const onAck = (data: any) => {
        // Check if this ack is for our health ping
        if (data.event_type === 'vo.health.ping') {
          cleanup();
          console.debug(`[SocketService] 🏥 [${namespace}] Health check ACK received`);
          resolve({ status: 'healthy' });
        }
      };

      const onNack = (data: any) => {
        // Check if this nack is for our health ping
        if (data.event_type === 'vo.health.ping') {
          cleanup();
          console.error(
            `[SocketService] 🏥 [${namespace}] Health check NACK:`,
            data?.message || data
          );
          reject(new Error(`Health check failed: ${data?.message || 'Unknown error'}`));
        }
      };

      const onDisconnect = () => {
        cleanup();
        reject(new Error(`Health check failed: disconnected from ${namespace}`));
      };

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error(`Health check timeout for ${namespace}`));
      }, timeoutMs);

      // Create and emit proper HealthPingEvent
      try {
        const healthEvent = new HealthPingEvent({
          timestamp: new Date().toISOString(),
        });

        console.debug(`[SocketService] 🏥 [${namespace}] Sending health ping...`);

        // Use the proper event emission system
        this.emitEvent(namespace, healthEvent).catch((emitError) => {
          cleanup();
          reject(new Error(`Health check emit failed: ${emitError.message}`));
        });

        // Set up response listeners
        socket.once('ack', onAck);
        socket.once('nack', onNack);
        socket.once('disconnect', onDisconnect);
      } catch (createError: any) {
        cleanup();
        reject(new Error(`Health check creation failed: ${createError.message}`));
      }
    });
  }

  /**
   * Detect if response is a no-handler error
   */
  private isNoHandlerError(envelope: EventEnvelope<any>): boolean {
    return (
      envelope.event_type === 'vo.system.no_handler' && envelope.data.payload?.event_type // Contains the original event type
    );
  }

  /**
   * Handle no-handler errors
   */
  private handleNoHandlerError(envelope: EventEnvelope<any>): void {
    const originalEventType = envelope.data.payload.event_type;

    // Add to no-handler set (if not already there)
    const wasAlreadyKnown = this.noHandlerEventTypes.has(originalEventType);
    this.noHandlerEventTypes.add(originalEventType);

    // Only set initial count if this is the first time we've seen this event
    if (!wasAlreadyKnown) {
      this.noHandlerAttempts.set(originalEventType, 1);
      console.warn(
        `[SocketService] 📝 Marking ${originalEventType} as no-handler (first detection)`
      );
    } else {
      // If already known, just log without changing count
      const currentAttempts = this.noHandlerAttempts.get(originalEventType) || 0;
      console.debug(
        `[SocketService] 📝 Received duplicate no-handler for ${originalEventType} (${currentAttempts} total attempts)`
      );
    }

    // Forward the NoHandlerEvent to EventService
    const noHandlerEvent = envelope.data.payload as BaseEvent;
    this.eventHandlers.get('/events')?.(noHandlerEvent);
  }

  // ===================================================================
  // MANAGEMENT & DEBUGGING METHODS
  // ===================================================================

  /**
   * Management methods for no-handler cache
   */
  clearNoHandlerCache(): void {
    this.noHandlerEventTypes.clear();
    this.noHandlerAttempts.clear();
    Logger.debug('[SocketService] 🧹 Cleared no-handler cache');
  }

  getNoHandlerEventTypes(): string[] {
    return Array.from(this.noHandlerEventTypes);
  }

  removeFromNoHandlerCache(eventType: string): void {
    this.noHandlerEventTypes.delete(eventType);
    this.noHandlerAttempts.delete(eventType);
    Logger.debug(`[SocketService] ♻️ Removed ${eventType} from no-handler cache`);
  }

  /**
   * Deduplication management methods
   */
  clearDeduplicationCache(): void {
    this.processedEvents.clear();
    this.eventTimestamps.clear();
    Logger.debug('[SocketService] 🧹 Cleared deduplication cache');
  }

  getDeduplicationStats(): { [socketId: string]: { eventCount: number; oldestEvent: number } } {
    const stats: { [socketId: string]: { eventCount: number; oldestEvent: number } } = {};

    this.processedEvents.forEach((events, socketId) => {
      const timestamps = this.eventTimestamps.get(socketId);
      const oldestTime = timestamps ? Math.min(...timestamps.values()) : Date.now();

      stats[socketId] = {
        eventCount: events.size,
        oldestEvent: oldestTime,
      };
    });

    return stats;
  }

  clearDeduplicationForSocket(socketId: string): void {
    this.cleanupSocketDeduplication(socketId);
    Logger.debug(`[SocketService] 🧹 Cleared deduplication for socket: ${socketId}`);
  }

  /**
   * Get comprehensive service statistics
   */
  getServiceStats(): {
    deduplication: { [socketId: string]: { eventCount: number; oldestEvent: number } };
    sessionFiltering: { filteredEvents: number };
    noHandler: { eventTypes: string[]; totalAttempts: number };
    connections: { total: number; namespaces: string[] };
    workers: { [namespace: string]: { connected: boolean; workerReady: boolean } };
  } {
    const noHandlerStats = Array.from(this.noHandlerAttempts.values()).reduce(
      (sum, attempts) => sum + attempts,
      0
    );
    const workerStats: { [namespace: string]: { connected: boolean; workerReady: boolean } } = {};
    Array.from(this.sockets.keys()).forEach((namespace) => {
      const workerStatus = WorkerSocketService.getInstance(namespace).getConnectionStatus();
      workerStats[namespace] = {
        connected: workerStatus.connected,
        workerReady: workerStatus.workerReady,
      };
    });

    return {
      deduplication: this.getDeduplicationStats(),
      sessionFiltering: this.getSessionFilteringStats(),
      noHandler: {
        eventTypes: this.getNoHandlerEventTypes(),
        totalAttempts: noHandlerStats,
      },
      connections: {
        total: this.sockets.size,
        namespaces: Array.from(this.sockets.keys()),
      },
      workers: workerStats,
    };
  }

  /**
   * Cleanup method for service shutdown.
   * After this is called, the service is permanently inert — no new sockets
   * can be created and no further events will be dispatched until the page reloads.
   */
  destroy(): void {
    this.isDestroyed = true;

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }

    this.sockets.forEach((socket, namespace) => {
      try {
        // Strip handlers first so any in-flight or buffered messages can't fire.
        socket.removeAllListeners();
      } catch (err) {
        console.warn('[SocketService] removeAllListeners failed during destroy:', err);
      }
      socket.disconnect();
      WorkerSocketService.getInstance(namespace).disconnect();
    });

    this.sockets.clear();
    this.eventHandlers.clear();
    this.subscribedTopics.clear();
    this.subscriptionInitialized = false;
    this.clearDeduplicationCache();
    this.clearNoHandlerCache();
    this.resetSessionFilteringStats();

    Logger.debug('[SocketService] 🛑 Service destroyed and cleaned up (permanently inert)');
  }
}

// ===================================================================
// EXPORTS
// ===================================================================

export const GlobalSocketService = SocketService.getInstance();
